import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { firstValueFrom, switchMap } from 'rxjs';

import { AskTemplatesDataService } from '@core/ask-hr-templates-logic';
import { AskHrChatFeatureId } from '@core/ask-hr-types';
import {
  type AnyChatComponentProvider,
  HrAssistantChatFeatureService,
  provideChatComponents,
} from '@core/intelligence-smart-ui';

import { ChatFormItemComponent } from '../chat-form-item/chat-form-item.component';

interface RequestTicketChatComponentConfig {
  name: 'request-form';
  props: {
    id: number;
  };
}

@Component({
  selector: 'ask-chat-request-item-dynamic',
  imports: [ChatFormItemComponent],
  template: `
    @if (template(); as templateValue) {
      <ask-chat-form-item [template]="templateValue" (click)="onClick()" margin-top-small />
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatFormItemDynamicComponent {
  private readonly requestTicketService = inject(AskTemplatesDataService);
  private readonly hrAssistantFeaturesService = inject(HrAssistantChatFeatureService);

  public props = input.required<RequestTicketChatComponentConfig['props']>();

  protected templateId = computed(() => this.props().id);
  protected template = toSignal(
    toObservable(this.templateId).pipe(
      switchMap(id => this.requestTicketService.getTemplateById(id)),
    ),
    { initialValue: null },
  );

  private loadTemplate = effect(() =>
    firstValueFrom(this.requestTicketService.loadTemplateById(this.props().id)),
  );

  protected onClick() {
    this.hrAssistantFeaturesService.setFeatureById(AskHrChatFeatureId.Form, {
      templateId: this.props().id,
    });
  }
}

export const provideAssistantChatRequestItem = provideChatComponents({
  useValue: {
    name: 'request-form',
    component: ChatFormItemDynamicComponent,
  } satisfies AnyChatComponentProvider<RequestTicketChatComponentConfig>,
});
